import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Edit, Circle } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DevicesIcon from "@mui/icons-material/Devices";

import {
  CalendarIcon,
  DetailsIcon,
  RatingIcon,
  TrendUpIcon,
  WalletIcon,
} from "../../components/icons";

import {
  PilotActivity
} from "./tabs/Activity";
import {
  PilotAssignments
} from "./tabs/Assignments";
import {
  PilotPayments
} from "./tabs/Payments";
import {
  PilotReviews
} from "./tabs/Reviews";
import PilotOverview, { type UserDetails as CrewUserDetails } from "./tabs/Overview";
import Availability from "./tabs/Availability";
import { PilotOnboarding } from "./tabs/Onboarding";
import EditCrewDialog from "./EditCrew";
import { PilotAssignmentDialog } from "./components";
import { PilotStatistics } from "./components";
import apiService from "../../service/Api";
import { RootState } from "../../store/Index";

import { StreamsIcon } from "../../components/icons";
import { PilotAPIResponse, PilotProps } from "./types/view-pilot";
import { EditAvatar } from "./components/EditProfilePicture";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { WhatsappMessageDialog } from "./components/WhatsappMessageDialog";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const ViewCrewMember: React.FC = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPilotAssignmentDialogOpen, setIsPilotAssignmentDialogOpen] =
    useState(false);
  const [isWhatsappDialogOpen, setIsWhatsappDialogOpen] = useState(false);
  const [isDeviceInfoOpen, setIsDeviceInfoOpen] = useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };

  const handlePilotAssignmentClick = () => {
    setIsPilotAssignmentDialogOpen(true);
  };
  const handleClosePilotAssignmentDialog = () => {
    setIsPilotAssignmentDialogOpen(false);
  };

  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const { id } = useParams<{ id: string }>();
  const [member, setMember] = useState<PilotProps | null>(null);
  const [lastLogin, setLastLogin] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<CrewUserDetails | null>(null);

  const fetchMember = useCallback(async () => {
    try {
      const pilot = await apiService.get<PilotAPIResponse>(
        `/api/v1/crews/crew-members/${id}/`,
      );

      setMember({
        id: pilot.data.id,
        user_id: pilot.data.user.id,
        average_rating: pilot.data.average_rating,
        rating_count: pilot.data.rating_count,
        pilot_type: pilot.data.user.user_type,
        name: pilot.data.user.first_name || "",
        surname: pilot.data.user.last_name || "",
        email: pilot.data.user.email || "",
        username: pilot.data.user.username || "",
        phone: pilot.data.user?.phone_number || "",
        organization: pilot.data.user?.organization || "",
        date_of_birth: pilot.data.user.date_of_birth || "",
        role: pilot.data.user.role,
        department: pilot.data.roles_detail[0]?.name || "",
        available_status: pilot.data.available_status,
        avatar: pilot.data.user.profile_picture || "",
        joinDate: format(new Date(pilot.data.created_at), "dd MMM, yyyy"),
        totalEarnings: 0, // Not available in response, calculate these on the fly
        totalAssignments: 0, // Not available in response, calculate these on the fly
        averageRating: 0, // Not available in response, calculate these on the fly
        lastGig: "N/A", // Not available in response, calculate these on the fly
        nextGig: "N/A", // Not available in response, calculate these on the fly
        roles: pilot.data.roles,
        skills: pilot.data.skills,
        tools: pilot.data.tools,
        rolesDetail: pilot.data.roles_detail || [],
        skillsDetail: pilot.data.skills_detail || [],
        toolsDetail: pilot.data.tools_detail || [],
        completeness_score: pilot.data.completeness_score ?? 0,
        crewFields: pilot.data.crew_fields || { filled_fields: [], null_fields: [] },
        documents: pilot.data.document_certifications || [], // Not available in response
        emergencyContact: pilot.data.user.emergency_contact_phone || "",
        emergencyContactName: pilot.data.user.emergency_contact || "",
        street_address: pilot.data.user.street_address || "",
        maritalStatus: pilot.data.user.marital_status || "",
        occupation: pilot.data.user.occupation || "",
        nationalId: pilot.data.user.national_id || "",
        city: pilot.data.user.city || "",
        state: pilot.data.user.state_province || "",
        postal_code: pilot.data.user.postal_code || "",
        country: pilot.data.user.residence_country || "",
        preferences: pilot.data.user.service_preferences || "",
        bio: pilot.data.bio || "",
        experience_years: pilot.data.experience_years || 0,
      });

      setLastLogin(pilot.data.account_summary?.last_login || null);
      const userResponse = await apiService.get<CrewUserDetails>(
        `/api/authentication/users/${pilot.data.user.id}/`
      );
      setUserDetails(userResponse.data);


    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to load crew member. Please try again.";
        toast.error(errorMessage);
      } else {
        console.error("Failed to fetch member", error);
      }
    } finally {
      // no-op
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchMember();
    }
  }, [fetchMember, id]);

  if (!id || !member) return null;

  const hasPhone = Boolean(member.phone?.trim());
  const hasDeviceInfo = Boolean(userDetails?.user_infor?.has_pixel_pilot_device);
  const deviceInfoRows = [
    ["Platform", userDetails?.user_infor?.device_information?.platform],
    ["Manufacturer", userDetails?.user_infor?.device_information?.manufacturer],
    ["Device Model", userDetails?.user_infor?.device_information?.device_model],
    ["OS Version", userDetails?.user_infor?.device_information?.os_version],
    ["App Version", userDetails?.user_infor?.device_information?.app_version],
  ].filter(([, value]) => Boolean(value));
  const crewDisplayName = [member.name, member.surname]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ minHeight: "100vh", position: "relative" }}>
        {/* Pilot Banner */}
        <Card
          sx={{
            mb: "16px",
            boxShadow: 2,
            height: "100%",
            position: "sticky",
            transition: "1s ease-in-out",
            zIndex: 9,
            top: -16,
          }}
        >
          <CardContent>
            <Grid container>
              <Grid size={{ lg: 1 }}>
                <EditAvatar
                  fetchMember={fetchMember}
                  initialImage={member?.avatar}
                  pilot={{
                    id: member?.user_id,
                    email: member?.email,
                    name: member?.name,
                  }}
                />
              </Grid>
              <Grid size={{ lg: 11 }}>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {member?.name} {member?.surname}
                  </Typography>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    gap={"8px"}
                  >
                    <Button
                      variant="outlined"
                      onClick={handlePilotAssignmentClick}
                      startIcon={
                        <StreamsIcon color="inherit" sx={{ fontSize: 20 }} />
                      }
                      sx={{
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    >
                      Assign to Event
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={!hasPhone}
                      onClick={() => setIsWhatsappDialogOpen(true)}
                      title={
                        hasPhone
                          ? "Send a WhatsApp message"
                          : "No phone number on file"
                      }
                      startIcon={
                        <WhatsAppIcon
                          sx={{
                            fontSize: 20,
                            color: hasPhone ? "#25D366" : "inherit",
                          }}
                        />
                      }
                      sx={{
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={handleEditClick}
                      sx={{
                        backgroundColor: reduxTheme.primary_colors[400],
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                </Box>
                <Chip
                  label={"Active"}
                  icon={<Circle color={"success"} sx={{ width: "8px" }} />}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    px: 0.5,
                    borderColor: "#4BA66340",
                    bgcolor: "#4BA66340",
                    color: "#4BA663",
                  }}
                />
                <Box mt={1} display={"flex"} alignItems={"center"} gap={0.4}>
                  {member.rolesDetail?.length !== 0 &&
                    member.rolesDetail.map((role) => {
                      return (
                        <Chip
                          label={role.name}
                          key={role.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: 1.5,
                            borderColor: reduxTheme.primary_colors[800],
                          }}
                        />
                      );
                    })}
                </Box>
                  <Box mt={1} display={"flex"} alignItems={"center"} gap={2}>
        
                     <Box mt={1} display={"flex"} alignItems={"center"} gap={2}>
                    <CalendarIcon sx={{ fontSize: 20 }} color="disabled" />
                    <Typography variant={"body2"}>{`Joined ${member?.joinDate}`}</Typography>
                  </Box>
                   <Box mt={1} display={"flex"} alignItems={"center"} gap={2}>
                    <CalendarIcon sx={{ fontSize: 20 }} color="disabled" />
                    <Typography variant={"body2"}>{`Last Login: ${lastLogin ? format(new Date(lastLogin), "dd MMM, yyyy, h:mm a") : "N/A"}`}</Typography>
                  </Box>
                  <Box mt={1} display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!hasDeviceInfo}
                      startIcon={<DevicesIcon />}
                      onClick={() => setIsDeviceInfoOpen(true)}
                      sx={{ textTransform: "none", borderRadius: 1.5 }}
                    >
                      Pilot&apos;s Device Info
                    </Button>
                  </Box>
                  </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <PilotStatistics pilot={member} />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            variant={"fullWidth"}
            value={tabValue}
            onChange={handleTabChange}
          >
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <DetailsIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Overview</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <StreamsIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Assignments</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <RatingIcon color={"disabled"} sx={{ fontSize: 22 }} />
                  <Typography variant={"body2"}>Reviews</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <TrendUpIcon color={"disabled"} sx={{ fontSize: 22 }} />
                  <Typography variant={"body2"}>Onboarding</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <WalletIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Payments</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <CalendarIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Availability</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <TrendUpIcon color={"disabled"} sx={{ fontSize: 22 }} />
                  <Typography variant={"body2"}>Activity</Typography>
                </Box>
              }
            />
          </Tabs>
        </Box>

        <AnimatePresence mode="wait">
          <TabPanel value={tabValue} index={0}>
            <PilotOverview
              pilot={member}
              userDetails={userDetails}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PilotAssignments pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <PilotReviews pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <PilotOnboarding pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <PilotPayments pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <Availability pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={6}>
            <PilotActivity pilot={member} />
          </TabPanel>
        </AnimatePresence>

        {/* DIALOGS */}
        <Dialog
          open={isDeviceInfoOpen}
          onClose={() => setIsDeviceInfoOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Device Information</DialogTitle>
          <DialogContent dividers>
            {deviceInfoRows.length > 0 ? (
              <Box display="grid" gap={1.5}>
                {deviceInfoRows.map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body1">{value}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body1">No device information available.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeviceInfoOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {member && (
          <>
            <EditCrewDialog
              open={isEditDialogOpen}
              onClose={handleCloseEditDialog}
              pilot={member}
            />

            <PilotAssignmentDialog
              open={isPilotAssignmentDialogOpen}
              onClose={handleClosePilotAssignmentDialog}
              pilot={member}
            />

            <WhatsappMessageDialog
              open={isWhatsappDialogOpen}
              onClose={() => setIsWhatsappDialogOpen(false)}
              phone={member.phone ?? ""}
              recipientName={crewDisplayName}
            />
          </>
        )}
      </Box>
    </motion.div>
  );
};

export default ViewCrewMember;
